/**
 * Weather Decision Criteria Configuration
 * 
 * SINGLE SOURCE OF TRUTH for GO/NO GO decision making.
 * Used by: FlightRouteWeather.jsx (logic) and AdminPage.jsx (info display)
 * 
 * When updating criteria, change values here and both components will update automatically.
 * 
 * Updated: March 2026
 */

export const WEATHER_CRITERIA = {
  // Aircraft limitations
  aircraft: {
    type: "DA42/DA62 MPP",
    crosswindLimit: 20, // knots
    crosswindUnit: "KTS",
  },

  // Airport reference
  airport: {
    icao: "EBOS",
    runway: "07/25",
    runwayHeadings: [70, 250], // degrees
  },

  // Visibility thresholds (VFR)
  visibility: {
    minVFR: 5000, // meters - minimum VFR visibility (5km)
    cautionMin: 5000, // meters - GO with caution threshold (5km)
    cautionMax: 10000, // meters - above this is clear (10km)
    poorThreshold: 5000, // meters - triggers CAUTION (5km)
    unit: "m",
  },

  // Cloud/ceiling thresholds
  clouds: {
    // Minimum safe operations
    minSafeOperations: 2000, // feet - minimum for safe operations (low clouds)
    
    // Scattered ceiling over sea waypoints
    scatteredMinOverSea: 1500, // feet - scattered min altitude for waypoints
    scatteredMinSmallPart: 1000, // feet - if only on small part of routing
    
    // Few/Scattered thresholds
    fewAcceptable: 3000, // feet - FEW below 3000ft is acceptable
    scatteredCaution: 3000, // feet - SCT at 3000ft is GO with caution
    
    lowCeilingThreshold: 2000, // feet AGL - minimum for safe operations
    icingCheckRange: [0, 2500], // feet - range to check for icing + clouds
    unit: "ft",
  },

  // Route waypoint weather checking
  routeWeather: {
    waypointRadius: 10, // nautical miles - check weather in 10nm radius
    showCoordinates: false, // don't show coordinates for waypoints
  },

  // IFR policy
  ifr: {
    allowDeparture: true, // IFR approved if can get out after takeoff
    description: "IFR is approved if we can get out of IFR conditions after take off and does not affect the rest of the routing",
  },

  // METAR timing
  metar: {
    validityWindow: 30, // minutes before mission start
  },

  // Flight categories
  flightCategories: {
    VFR: { ceiling: ">3000ft", visibility: ">5km", color: "#10b981" },
    MVFR: { ceiling: "1000-3000ft", visibility: "3-5km", color: "#f59e0b" },
    IFR: { ceiling: "<1000ft", visibility: "<3km", color: "#dc2626" },
    LIFR: { ceiling: "<500ft", visibility: "<1km", color: "#7c3aed" },
  },

  // Special airport configurations
  specialAirports: {
    OSPREY4: {
      departureAirport: "LEMG", // Always starts from LEMG
      alwaysShowMetar: true,
    },
  },
};

export const DECISION_CRITERIA = {
  // NO GO - Immediate rejection criteria
  noGo: [
    {
      id: "ifr_lifr_enroute",
      name: "IFR / LIFR Conditions En-Route",
      description: "TAF forecasts IFR (ceiling <1000ft or vis <3km) or LIFR (ceiling <500ft or vis <1km) along the routing (excluding departure if breakout possible)",
      reason: "VFR patrol not possible. IFR allowed at departure only if breakout is possible after takeoff.",
      icon: "Cloud",
    },
    {
      id: "thunderstorms",
      name: "Thunderstorms / CB Along Routing",
      description: "TAF contains TS (thunderstorm) or CB (cumulonimbus) in any forecast period covering mission window",
      reason: "Mission cancelled due to convective activity.",
      icon: "Zap",
    },
    {
      id: "crosswind",
      name: `Crosswind > ${WEATHER_CRITERIA.aircraft.crosswindLimit} ${WEATHER_CRITERIA.aircraft.crosswindUnit} at ${WEATHER_CRITERIA.airport.icao}`,
      description: `Wind or gusts exceeding ${WEATHER_CRITERIA.aircraft.crosswindLimit} knots crosswind component (runway ${WEATHER_CRITERIA.airport.runway} = ${WEATHER_CRITERIA.airport.runwayHeadings[0]}°/${WEATHER_CRITERIA.airport.runwayHeadings[1]}°)`,
      reason: "Crosswind = Wind Speed × sin(|Wind Direction - Runway Heading|)",
      icon: "Wind",
    },
    {
      id: "visibility_below_min",
      name: `Visibility < ${WEATHER_CRITERIA.visibility.minVFR/1000}km (Min VFR)`,
      description: `Visibility below minimum VFR threshold of ${WEATHER_CRITERIA.visibility.minVFR/1000}km`,
      reason: "Visibility below legal VFR minimums",
      icon: "Eye",
    },
    {
      id: "icing_clouds",
      name: `Known Icing + Clouds ${WEATHER_CRITERIA.clouds.icingCheckRange[0]}-${WEATHER_CRITERIA.clouds.icingCheckRange[1]}ft`,
      description: `Icing conditions (freezing level ≤ ${WEATHER_CRITERIA.clouds.icingCheckRange[1]}ft or temp near 0°C with moisture) AND clouds present in ${WEATHER_CRITERIA.clouds.icingCheckRange[0]}-${WEATHER_CRITERIA.clouds.icingCheckRange[1]}ft range`,
      reason: "Ice accretion risk in operating altitude band.",
      icon: "Thermometer",
    },
    {
      id: "low_clouds_unsafe",
      name: `Clouds Below ${WEATHER_CRITERIA.clouds.minSafeOperations}ft (Unsafe)`,
      description: `Cloud ceiling below ${WEATHER_CRITERIA.clouds.minSafeOperations}ft AGL - minimum for safe operations`,
      reason: "Insufficient vertical clearance for safe patrol operations",
      icon: "Cloud",
    },
  ],

  // GO WITH CAUTION criteria
  caution: [
    {
      id: "visibility_marginal",
      name: `Marginal Visibility (${WEATHER_CRITERIA.visibility.cautionMin/1000}-${WEATHER_CRITERIA.visibility.cautionMax/1000}km)`,
      description: "Visibility between 5-10km - GO with caution",
      icon: "Eye",
    },
    {
      id: "scattered_3000",
      name: `Scattered Clouds at ${WEATHER_CRITERIA.clouds.scatteredCaution}ft`,
      description: `Scattered (SCT) cloud layer at ${WEATHER_CRITERIA.clouds.scatteredCaution}ft - GO with caution`,
      icon: "Cloud",
    },
    {
      id: "scattered_sea_1500",
      name: `Scattered Over Sea Waypoints (${WEATHER_CRITERIA.clouds.scatteredMinOverSea}ft min)`,
      description: `Scattered clouds over sea waypoints - minimum ${WEATHER_CRITERIA.clouds.scatteredMinOverSea}ft, ${WEATHER_CRITERIA.clouds.scatteredMinSmallPart}ft if small portion of route`,
      icon: "Cloud",
    },
    {
      id: "icing_no_clouds",
      name: `Known Icing (without clouds ${WEATHER_CRITERIA.clouds.icingCheckRange[0]}-${WEATHER_CRITERIA.clouds.icingCheckRange[1]}ft)`,
      description: "Icing possible but no clouds in operational altitude band",
      icon: "Thermometer",
    },
    {
      id: "mvfr",
      name: "MVFR Conditions (current)",
      description: "Marginal VFR - ceiling 1000-3000ft or visibility 3-5km",
      icon: "CloudRain",
    },
    {
      id: "turbulence",
      name: "Moderate Turbulence (EDR)",
      description: "Turbulence EDR indicates moderate conditions along routing",
      icon: "Wind",
    },
  ],

  // GO criteria (all must be true)
  go: [
    {
      id: "vfr",
      name: "TAF forecasts VFR throughout mission window (or IFR departure with breakout)",
    },
    {
      id: "no_ts_cb",
      name: "No thunderstorms (TS) or cumulonimbus (CB) forecast",
    },
    {
      id: "crosswind_ok",
      name: `Crosswind ≤ ${WEATHER_CRITERIA.aircraft.crosswindLimit} ${WEATHER_CRITERIA.aircraft.crosswindUnit} at departure airport`,
    },
    {
      id: "no_icing",
      name: `No icing conditions in ${WEATHER_CRITERIA.clouds.icingCheckRange[0]}-${WEATHER_CRITERIA.clouds.icingCheckRange[1]}ft with clouds`,
    },
    {
      id: "visibility_ok",
      name: `Visibility ≥ ${WEATHER_CRITERIA.visibility.minVFR/1000}km (VFR minimum)`,
    },
    {
      id: "ceiling_ok",
      name: `Ceiling ≥ ${WEATHER_CRITERIA.clouds.minSafeOperations}ft (safe operations)`,
    },
    {
      id: "few_acceptable",
      name: `FEW below ${WEATHER_CRITERIA.clouds.fewAcceptable}ft is acceptable`,
    },
  ],
};

export const PATROL_EFFECTIVENESS = {
  effective: {
    label: "EFFECTIVE",
    description: "VFR conditions, visibility >10km, camera operations optimal",
    color: "emerald",
  },
  reduced: {
    label: "REDUCED", 
    description: "MVFR or visibility 5-10km, camera operations degraded",
    color: "amber",
  },
  ineffective: {
    label: "INEFFECTIVE",
    description: "IFR/LIFR conditions or visibility <5km, camera cannot operate",
    color: "red",
  },
};

export const DATA_SOURCES = [
  { name: "METAR/TAF", source: "Aviation Weather Center (aviationweather.gov)" },
  { name: "Freezing Level", source: "Windy API (GFS model)" },
  { name: "Weather Maps", source: "Windy.com embedded layers" },
  { name: "Turbulence EDR", source: "Windy API" },
  { name: "AI Analysis", source: "OpenAI GPT-5.2 via Emergent LLM" },
];

// Technical parameters for quick reference
export const TECHNICAL_PARAMS = [
  { label: "Crosswind Limit", value: `${WEATHER_CRITERIA.aircraft.crosswindLimit} ${WEATHER_CRITERIA.aircraft.crosswindUnit}` },
  { label: "Min VFR Visibility", value: `≥ ${WEATHER_CRITERIA.visibility.minVFR/1000}km` },
  { label: "Caution Visibility", value: `${WEATHER_CRITERIA.visibility.cautionMin/1000}-${WEATHER_CRITERIA.visibility.cautionMax/1000}km` },
  { label: "Min Safe Ceiling", value: `≥ ${WEATHER_CRITERIA.clouds.minSafeOperations}${WEATHER_CRITERIA.clouds.unit} AGL` },
  { label: "FEW Acceptable", value: `< ${WEATHER_CRITERIA.clouds.fewAcceptable}${WEATHER_CRITERIA.clouds.unit}` },
  { label: "SCT Caution", value: `${WEATHER_CRITERIA.clouds.scatteredCaution}${WEATHER_CRITERIA.clouds.unit}` },
  { label: "SCT Over Sea Min", value: `${WEATHER_CRITERIA.clouds.scatteredMinOverSea}${WEATHER_CRITERIA.clouds.unit} (${WEATHER_CRITERIA.clouds.scatteredMinSmallPart}ft small part)` },
  { label: "Waypoint Check Radius", value: `${WEATHER_CRITERIA.routeWeather.waypointRadius}nm` },
  { label: "Icing Check Range", value: `${WEATHER_CRITERIA.clouds.icingCheckRange[0]} - ${WEATHER_CRITERIA.clouds.icingCheckRange[1]}${WEATHER_CRITERIA.clouds.unit}` },
  { label: `${WEATHER_CRITERIA.airport.icao} Runway`, value: `${WEATHER_CRITERIA.airport.runway} (${WEATHER_CRITERIA.airport.runwayHeadings[0]}°/${WEATHER_CRITERIA.airport.runwayHeadings[1]}°)` },
  { label: "METAR Validity", value: `≤ ${WEATHER_CRITERIA.metar.validityWindow}min before mission` },
  { label: "IFR Policy", value: WEATHER_CRITERIA.ifr.description },
  { label: "OSPREY 4 Departure", value: `Always ${WEATHER_CRITERIA.specialAirports.OSPREY4.departureAirport}` },
];
